import React, { useState, useEffect } from 'react';
import { createSpiritualFamily, getSpiritualFathers, getMembers } from '../../services/api';
import { useNavigate, useSearchParams } from 'react-router-dom';

const AddFamily = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedFatherId = searchParams.get('father_id') || '';

  const [form, setForm] = useState({ family_name: '', head_member_id: '', spiritual_father_id: preselectedFatherId });
  const [leaders, setLeaders] = useState([]);
  const [allMembers, setAllMembers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    getSpiritualFathers().then(r => setLeaders(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    getMembers({}).then(r => setAllMembers(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  const set = field => e => setForm(p => ({ ...p, [field]: e.target.value }));

  // Auto-fill family name when head member is selected
  const handleHeadChange = (e) => {
    const memberId = e.target.value;
    setForm(p => {
      const member = allMembers.find(m => String(m.id) === String(memberId));
      const autoName = member ? `${member.first_name} ${member.last_name}'s Family` : p.family_name;
      return { ...p, head_member_id: memberId, family_name: autoName };
    });
  };

  const handleSubmit = async () => {
    setError('');
    if (!form.family_name) return setError('Family name is required');
    if (!form.spiritual_father_id) return setError('Please assign a spiritual leader to this family');
    setSaving(true);
    try {
      await createSpiritualFamily({
        family_name: form.family_name,
        head_member_id: form.head_member_id || null,
        spiritual_father_id: form.spiritual_father_id || null,
      });
      navigate('/spiritual/families');
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to create family');
    } finally {
      setSaving(false);
    }
  };

  const selectedLeader = leaders.find(l => String(l.id) === String(form.spiritual_father_id));

  return (
    <div className="page-content">
      <div className="page-header">
        <div><h1>Register Family</h1><p>Add a new family under a spiritual leader</p></div>
        <button className="btn btn-secondary" onClick={() => navigate('/spiritual/families')}>← Back</button>
      </div>

      {leaders.length === 0 && (
        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '12px 16px', marginBottom: 20, fontSize: 13 }}>
          <strong>No spiritual leaders registered yet.</strong>{' '}
          <span style={{ color: '#4f46e5', cursor: 'pointer', textDecoration: 'underline' }} onClick={() => navigate('/spiritual/fathers')}>
            Register a leader first
          </span>.
        </div>
      )}

      <div className="card" style={{ maxWidth: 600 }}>
        <div className="card-body">
          {error && <div className="error-msg">{error}</div>}
          <div className="form-grid">

            {/* Spiritual Father — shown first, may be pre-filled */}
            <div className="form-group form-full">
              <label>Spiritual Father <span className="required">*</span></label>
              {selectedLeader && preselectedFatherId ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', background: '#eef2ff', border: '1px solid #a5b4fc', borderRadius: 8 }}>
                  <span style={{ fontWeight: 600, color: '#4f46e5' }}>{selectedLeader.first_name} {selectedLeader.last_name}</span>
                  <span style={{ fontSize: 12, color: '#6b7280' }}>{selectedLeader.family_count} families</span>
                  <button className="btn btn-sm btn-secondary" style={{ marginLeft: 'auto' }} onClick={() => setForm(p => ({ ...p, spiritual_father_id: '' }))}>Change</button>
                </div>
              ) : (
                <select value={form.spiritual_father_id} onChange={set('spiritual_father_id')}>
                  <option value="">— Select spiritual leader —</option>
                  {leaders.map(f => (
                    <option key={f.id} value={f.id}>
                      {f.first_name} {f.last_name} ({f.family_count} {f.family_count === 1 ? 'family' : 'families'})
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Head of Family */}
            <div className="form-group form-full">
              <label>Head of Family</label>
              <select value={form.head_member_id} onChange={handleHeadChange}>
                <option value="">— Select member —</option>
                {allMembers.map(m => <option key={m.id} value={m.id}>{m.first_name} {m.last_name}</option>)}
              </select>
              <small style={{ color: '#6b7280', fontSize: 11 }}>Selecting the head will auto-fill the family name.</small>
            </div>

            {/* Family Name — auto-filled but editable */}
            <div className="form-group form-full">
              <label>Family Name <span className="required">*</span></label>
              <input value={form.family_name} onChange={set('family_name')} placeholder="e.g. Bekele Family" />
            </div>

          </div>

          <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
            <button className="btn btn-primary" onClick={handleSubmit} disabled={saving || leaders.length === 0}>
              {saving ? 'Saving...' : 'Register Family'}
            </button>
            <button className="btn btn-secondary" onClick={() => navigate('/spiritual/families')}>Cancel</button>
          </div>

          <p style={{ fontSize: 12, color: '#6b7280', marginTop: 16 }}>
            After registering, open the family to add dependents (wife, children) and record follow-ups.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AddFamily;
